import Table from '..'
import { useTranslation } from 'react-i18next'
import { useContext } from 'react'
import { useSociogramActions } from '@BUP/hooks/Sociogram/useSociogramContext.js'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext.jsx'
import { multiCourse } from '@BUP/constants/index.js'
import IconCell from './icon-cell.jsx'
import NameCell from './name-cell.jsx'
import styles from './sociogram-table.module.scss'
import StatusCell from './status-cell.jsx'
import ParticipantsCell from './participants-cell.jsx'
import ActionsCell from './actions-cell.jsx'
import { useTeacherResultsColumn } from './useTeacherResultsColumn'
import { hasNonEmptyArray } from '@BUP/utils/table-validations'

const SociogramTable = ({
  surveysData,
  loading,
  error,
  selectedEvent
}) => {
  const i18n = useTranslation()

  const { setSelectedSurvey } = useSociogramActions()
  const organization = useContext(OrganizationContext)

  const { teacherResultsColumn, teacherAccessByCourseId } = useTeacherResultsColumn({ selectedEvent })

  if ((!surveysData || surveysData.length === 0) && !loading) return null

  const { type: organizationType } = organization
  const isMultiCourse = organizationType === multiCourse

  const setFontColor = (isActive, isDone, results = []) => {
    if (!isActive && isDone && results.length === 0) return '#17BEBB'
    if (!isActive && isDone && results.length > 0) return '#6441A5'
    return '#898A89'
  }

  const setFontWeight = (isActive, isDone, results) => {
    if (!isActive && isDone && results.length > 0) return 'bold'
    if (!isActive && isDone && results.length === 0) return 'bold'
    return '400'
  }

  const columns = [
    {
      Header: '',
      accessor: 'id',
      disableSortBy: true,
      Cell: ({ row }) => {
        const { original: survey } = row
        return <IconCell survey={survey} />
      }
    },
    {
      Header: isMultiCourse
        ? i18n.t('common.resources.course.attributes.tags.nivel.label')
        : i18n.t('common.resources.course.label'),
      accessor: 'name',
      Cell: ({ row }) => {
        const { original: survey } = row
        return (
          <NameCell
            survey={survey}
            isMultiCourse={isMultiCourse}
            setFontColor={setFontColor}
            setFontWeight={setFontWeight}
          />
        )
      }
    },
    {
      Header: `${i18n.t('pages.data.sociogram.activeSociogramEce.table.headerState')}`,
      accessor: 'isActive',
      disableSortBy: true,
      Cell: ({ value: isActive, row }) => {
        const { original: survey } = row
        return (
          <StatusCell
            survey={survey}
            isActive={isActive}
            setFontColor={setFontColor}
            setFontWeight={setFontWeight}
          />
        )
      }
    },
    {
      Header: `${i18n.t('pages.data.sociogram.activeSociogramEce.table.headerResponses')}`,
      accessor: 'participants',
      disableSortBy: true,
      Cell: ({ row, value: participants }) => {
        const { original: survey } = row
        if (!survey) {
          return (
            <span className={styles.actions_cell_text}>
              {i18n.t('pages.data.sociogram.activeSociogramEce.noSurveyMessage')}
            </span>
          )
        }
        const participantsTerminatedCount =
          survey?.participantsTerminated?.length ?? 0
        return (
          <ParticipantsCell
            survey={survey}
            participants={participants}
            setFontColor={setFontColor}
            setSelectedSurvey={setSelectedSurvey}
            participantsTerminated={participantsTerminatedCount}
          />
        )
      }
    },
    {
      Header: `${i18n.t('pages.data.sociogram.activeSociogramEce.table.actionsHeader')}`,
      accessor: 'isDone',
      disableSortBy: true,
      Cell: ({ row }) => {
        const { original: survey } = row
        if (!survey) {
          return (
            <span className={styles.actions_cell_text}>
              {i18n.t('pages.data.sociogram.activeSociogramEce.noSurveyMessage')}
            </span>
          )
        }
        const participantsTerminatedCount =
          survey?.participantsTerminated?.length ?? 0

        return (
          <ActionsCell
            survey={survey}
            isMultiCourse={isMultiCourse}
            error={error}
            participantsTerminated={participantsTerminatedCount}
          />
        )
      }
    }
  ]

  const hasTeacherDataAndResults = surveysData.some(
    (survey) => {
      const surveyCourseId = survey.courses?.[0]?.id
      const teacherAccessEntry = teacherAccessByCourseId.get(surveyCourseId)

      return (
        teacherAccessEntry?.teachers?.length > 0 &&
        hasNonEmptyArray(survey.results)
      )
    }
  )

  if (!isMultiCourse && hasTeacherDataAndResults) {
    columns.push(teacherResultsColumn)
  }

  return (
    <div data-testid='sociogramInnerTable'>
      <Table
        data={surveysData}
        columnsArray={columns}
        smallFirstChild
        loading={loading}
      />
    </div>
  )
}

export default SociogramTable
